import { DecryptedContact, DecryptedPatient, Patient } from '@icure/cardinal-sdk/model.mjs'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { cardinalApi, CardinalApiState, guard } from '../services/auth.api'
import { loadFromIterator, tagsByIds } from './utils'
import { AccessLevel } from '@icure/cardinal-sdk'
import { ContactFilters } from '@icure/cardinal-sdk/filters.mjs'

// Walks a Contact's services and returns every Document id referenced by their
// content entries (e.g. PDF report + fundus images). Does not recurse into
// compoundValue sub-services — attached documents live on top-level services.
const extractDocumentIdsFromContact = (contact: DecryptedContact): string[] => {
  const ids = new Set<string>()
  for (const service of contact.services ?? []) {
    for (const content of Object.values(service.content ?? {})) {
      const id = content?.documentId
      if (id) ids.add(id)
    }
  }
  return Array.from(ids)
}

export const contactApiRtk = createApi({
  reducerPath: 'contactApi',
  tagTypes: ['Contact'],
  baseQuery: fetchBaseQuery({
    baseUrl: '',
  }),
  endpoints: (builder) => ({
    createContact: builder.mutation<DecryptedContact | undefined, { patient: DecryptedPatient; contact: DecryptedContact }>({
      async queryFn({ patient, contact }, { getState }) {
        const {
          cardinalApi: { user },
        } = getState() as { cardinalApi: CardinalApiState }
        const contactApi = (await cardinalApi(getState))?.contact
        return guard([contactApi, user], async ([contactApi, user]): Promise<DecryptedContact | undefined> => {
          const contactWithMetadata = await contactApi?.withEncryptionMetadata(contact, patient, {
            user,
            delegates: Object.fromEntries(
              Object.values(patient.securityMetadata?.secureDelegations ?? [])
                .map((d) => (d.delegate ? [d.delegate, AccessLevel.Write] : undefined))
                .filter((x) => !!x),
            ),
          })
          if (!contactWithMetadata) {
            throw new Error('Error while creating ContactWithMetadata')
          }
          return await contactApi?.createContact(contactWithMetadata)
        })
      },

      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'Contact', id: 'all' },
              { type: 'Contact', id: result.id },
            ]
          : [],
    }),
    modifyContact: builder.mutation<DecryptedContact | undefined, DecryptedContact>({
      async queryFn(contact, { getState }) {
        const contactApi = (await cardinalApi(getState))?.contact
        return guard([contactApi], async ([contactApi]): Promise<DecryptedContact | undefined> => {
          return await contactApi?.modifyContact(contact)
        })
      },

      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'Contact', id: 'all' },
              { type: 'Contact', id: result.id },
            ]
          : [],
    }),
    deleteContact: builder.mutation<string | undefined, DecryptedContact>({
      async queryFn(contact, { getState }) {
        const contactApi = (await cardinalApi(getState))?.contact
        const documentApi = (await cardinalApi(getState))?.document
        return guard([contactApi, documentApi], async ([contactApi, documentApi]): Promise<string | undefined> => {
          const documentIds = extractDocumentIdsFromContact(contact)
          if (documentIds.length > 0) {
            const documents = await documentApi.getDocuments(documentIds)
            if (documents.length > 0) {
              await documentApi.deleteDocuments(documents)
            }
          }
          const result = await contactApi.deleteContact(contact)
          if (!result) {
            throw new Error('Contact can`t be deleted')
          }
          return contact.id
        })
      },
      invalidatesTags: (result) =>
        !!result
          ? [
              { type: 'Contact', id: 'all' },
              { type: 'Contact', id: result },
            ]
          : [],
    }),
    findContactsByHcPartyPatient: builder.query<DecryptedContact[] | undefined, { hcPartyId: string; patient: Patient }>({
      async queryFn({ hcPartyId, patient }, { getState }) {
        const contactApi = (await cardinalApi(getState))?.contact
        return guard([contactApi], async ([contactApi]): Promise<DecryptedContact[]> => {
          return await loadFromIterator(await contactApi.filterContactsBy(ContactFilters.byPatientsForDataOwner(hcPartyId, [patient])), 1000)
        })
      },
      providesTags: tagsByIds('Contact', 'all'),
    }),
    findContactsWithFormIds: builder.query<DecryptedContact[] | undefined, { hcPartyId: string; formIds: string[] }>({
      async queryFn({ hcPartyId, formIds }, { getState }) {
        const contactApi = (await cardinalApi(getState))?.contact
        return guard([contactApi], async ([contactApi]): Promise<DecryptedContact[]> => {
          return await loadFromIterator(await contactApi.filterContactsBy(ContactFilters.byFormIdsForDataOwner(hcPartyId, formIds)), 1000)
        })
      },
      providesTags: tagsByIds('Contact', 'all'),
    }),
  }),
})

export const { useCreateContactMutation, useModifyContactMutation, useDeleteContactMutation, useFindContactsByHcPartyPatientQuery, useFindContactsWithFormIdsQuery } = contactApiRtk
